// /:partnerId/verify?token=...[&client_id&redirect_uri&state&code_challenge&scope&nonce&av]
// Completa la verificacion de email (2.12) y, en el caso web (mismo navegador),
// auto-login al partner.
//
// Llega aca clickeando el link del mail SES. Si el alta vino de un flow OAuth, el
// link tambien trae los authz params + un nonce `av`. La cookie `av` la seteo el
// SPA al registrar (mismo navegador). Logica:
//   - cookie `av` matchea el `av` del link  → mismo navegador (web): mandamos
//     los authz params al confirm → el backend mintea un code → redirect_to →
//     auto-login (vuelta al callback del partner).
//   - no matchea / falta (mail abierto en otro dispositivo: app/TV) → solo
//     confirmamos el email y mostramos "volve a la app a ingresar".
//
// Estados: loading | redirecting (navegando al callback) | success | error.

import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import AuthShell from '@/components/AuthShell';
import { btn } from '@/lib/button-styles';
import { confirmEmailVerification, ApiError, type VerifyAuthzParams } from '@/lib/api';
import { stashPayReturn } from '@/lib/pay-return';

type State =
  | { kind: 'loading' }
  | { kind: 'redirecting' }
  | { kind: 'success' }
  // Email verificado pero con pago pendiente: ofrecemos completar el pago.
  | { kind: 'payment-pending'; payToken: string | null }
  | { kind: 'error'; message: string };

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1] as string) : null;
}

export default function Verify(): JSX.Element {
  const { partnerId = '' } = useParams<{ partnerId: string }>();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setState({ kind: 'error', message: 'Falta el token en la URL.' });
      return;
    }

    // Authz params del link (presentes solo si el signup vino de un flow OAuth).
    const clientId = searchParams.get('client_id');
    const redirectUri = searchParams.get('redirect_uri');
    const oauthState = searchParams.get('state');
    const codeChallenge = searchParams.get('code_challenge');
    const scope = searchParams.get('scope');
    const nonce = searchParams.get('nonce');
    const av = searchParams.get('av');

    // Mismo navegador: la cookie `av` (seteada al registrar) matchea el `av`
    // del link. Solo en ese caso intentamos auto-login.
    const sameBrowser = av !== null && readCookie('av') === av;
    const hasAuthz = Boolean(clientId && redirectUri && oauthState && codeChallenge);
    const authz: VerifyAuthzParams | undefined =
      sameBrowser && hasAuthz
        ? {
            client_id: clientId as string,
            redirect_uri: redirectUri as string,
            state: oauthState as string,
            code_challenge: codeChallenge as string,
            code_challenge_method: 'S256',
            scope: scope ?? 'openid profile',
            ...(nonce ? { nonce } : {}),
          }
        : undefined;

    let cancelled = false;
    (async () => {
      try {
        const result = await confirmEmailVerification(partnerId, token, authz);
        if (cancelled) return;
        if (result.redirect_to) {
          // Auto-login: navegamos al callback del partner con el code.
          setState({ kind: 'redirecting' });
          window.location.href = result.redirect_to;
          return;
        }
        if (result.payment_pending) {
          // Email verificado pero el pago quedó pendiente: ofrecemos completarlo.
          // El `pt` (credencial de pago acotada) viaja en el link del mail.
          const pt = searchParams.get('pt');
          // Mismo navegador que el alta (cookie `av` ya validada arriba, que es
          // lo que `authz` estar definido significa) → dejamos el contexto de
          // retorno para /pay, así al confirmar el pago el backend puede mintear
          // el code y devolver al usuario a la app ya logueado. Sin esto el pago
          // desde el link del mail terminaba SIEMPRE en "listo, volvé a la app"
          // aunque fuese la misma persona en la misma pestaña.
          //
          // Si el mail se abrió en otro dispositivo/navegador, o si el link se lo
          // pasaron a un tercero para que pague, `authz` es undefined y no hay
          // redirect: el retorno automático es del titular, no del pagador.
          if (pt && authz) stashPayReturn(pt, authz);
          setState({ kind: 'payment-pending', payToken: pt });
          return;
        }
        setState({ kind: 'success' });
      } catch (err) {
        if (cancelled) return;
        const msg =
          err instanceof ApiError
            ? err.message
            : 'No pudimos verificar el email. El link puede estar vencido.';
        setState({ kind: 'error', message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [partnerId, searchParams]);

  if (state.kind === 'loading') {
    return (
      <AuthShell title="Verificando…" subtitle="Un momento por favor.">
        <div className="text-sm text-text-muted py-4 text-center">⏳</div>
      </AuthShell>
    );
  }

  if (state.kind === 'redirecting') {
    return (
      <AuthShell title="Email verificado ✓" subtitle="Ingresando…">
        <div className="text-sm text-text-muted py-4 text-center">⏳</div>
      </AuthShell>
    );
  }

  if (state.kind === 'payment-pending') {
    // Email verificado pero falta el pago (plan pago no completado). Ofrecemos
    // terminar el pago sin login vía el `pt` del link. Si por algo no vino el
    // `pt`, caemos a /pay (login fallback).
    const payHref = state.payToken
      ? `/${partnerId}/pay?pt=${encodeURIComponent(state.payToken)}`
      : `/${partnerId}/pay`;
    return (
      <AuthShell
        title="E-mail verificado ✓"
        subtitle="Te falta un paso: completar el pago."
      >
        <div
          role="status"
          className="rounded-lg border border-primary/30 bg-primary/10 p-4 text-sm text-text"
        >
          Verificamos tu e-mail. Para activar tu suscripción, completá el pago.
        </div>
        <a
          href={payHref}
          className={btn('primary', 'md', 'mt-3 w-full')}
        >
          Completá tu pago
        </a>
      </AuthShell>
    );
  }

  if (state.kind === 'success') {
    // Caso multi-dispositivo (mail abierto en otro lado que el registro): el
    // email quedo verificado pero no completamos OAuth aca. El usuario vuelve a
    // la app a iniciar sesion (que ahora funciona por estar verificado).
    return (
      <AuthShell title="E-mail verificado ✓" subtitle="Tu cuenta está activa.">
        <div
          role="status"
          className="rounded-lg border border-primary/30 bg-primary/10 p-4 text-sm text-text"
        >
          Verificamos tu e-mail. Ahora podés volver a la App e iniciar sesión.
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="No pudimos verificar"
      subtitle="El link es inválido o ya fue usado."
    >
      <div
        role="alert"
        className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error"
      >
        {state.message}
      </div>
      <p className="text-xs text-text-muted mt-3">
        Pedí un nuevo link desde la pantalla de login (link "Olvidé mi contraseña" si tu cuenta ya existe) o registrate de nuevo.
      </p>
    </AuthShell>
  );
}
